import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  LAYOUT,
  STONE_COLORS,
  findNumberLine,
  numberWord,
  padItemOffset,
  pathSlots,
} from "./gameUtils";


// The six Critter Crossing challenge templates, each played directly on the
// river stage in unit coordinates. Solving a challenge either builds the
// stepping-stone path itself (placeStones / addOneMore / colorPattern /
// numberHunt) or lets the page raise the reward path (compareGroups /
// countAnswer). Every wrong-tap on a miss-able template calls onMistake.

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const pct = (unit) => ({ left: `${unit.x * 100}%`, top: `${unit.y * 100}%` });

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

// The instruction box, drawn as the critter's own speech bubble: it sits
// directly above the waiting spot with its tail pointing down at the friend
// (the tail lives 22px from the bubble's left edge, so shift the bubble left
// by that much to line the tail up with the critter).
const PromptCapsule = ({ text, unit }) => (
  <motion.div
    className="cc-prompt"
    style={
      unit
        ? { left: `${unit.x * 100}%`, top: `${(unit.y - 0.3) * 100}%`, x: -31 }
        : undefined
    }
    initial={{ scale: 0.7, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: "spring", stiffness: 300, damping: 22 }}
  >
    <span>{text}</span>
    <span className="cc-prompt-tail" />
  </motion.div>
);

const SlotGlow = ({ unit, onTap, label }) => (
  <motion.button
    type="button"
    className="cc-slot-glow"
    style={pct(unit)}
    onClick={onTap}
    aria-label={label}
    initial={{ scale: 0.6, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0.6, opacity: 0 }}
  >
    <span className="cc-slot-ring" />
    <span className="cc-slot-spark">✦</span>
  </motion.button>
);

const CountPop = ({ pop }) => (
  <AnimatePresence>
    {pop && (
      <span key={pop.key} className="cc-count-pop-anchor" style={pct(pop.unit)}>
        <motion.span
          className="cc-count-pop"
          initial={{ scale: 0.2, y: -6, opacity: 0 }}
          animate={{ scale: 1, y: -48, opacity: 1 }}
          exit={{ y: -74, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 18 }}
        >
          {pop.number}
        </motion.span>
      </span>
    )}
  </AnimatePresence>
);

// Pad of little friends (compare + count). padWidth is in pixels.
const LilyPad = ({ padWidth, count, itemAsset, itemName, badge, glow, wiggleKey, celebrate, onTap, unit }) => (
  <motion.div
    className="cc-pad"
    style={{ ...pct(unit), width: padWidth, height: padWidth, marginLeft: -padWidth / 2, marginTop: -padWidth / 2 }}
    onClick={onTap}
    role={onTap ? "button" : undefined}
    aria-label={`Lily pad with ${count} ${itemName}`}
    animate={wiggleKey ? { x: [0, -10, 10, -7, 7, 0] } : {}}
    transition={{ duration: 0.45 }}
  >
    {glow && <span className="cc-pad-glow" />}
    <img className="cc-pad-img" src="/assets/games/critter-crossing/cc_lilypad.png" alt="" draggable="false" />
    {Array.from({ length: count }).map((_, i) => {
      const offset = padItemOffset(i, count);
      return (
        <motion.img
          key={i}
          className="cc-pad-item"
          src={itemAsset}
          alt=""
          draggable="false"
          style={{
            width: padWidth * 0.24,
            height: padWidth * 0.24,
            left: `${50 + offset.x * 100}%`,
            top: `${50 + offset.y * 100}%`,
            marginLeft: -padWidth * 0.12,
            marginTop: -padWidth * 0.12,
          }}
          animate={celebrate ? { scale: [1, 1.18, 1] } : {}}
          transition={{ duration: 0.4 }}
        />
      );
    })}
    {badge != null && (
      <motion.span
        className={`cc-pad-badge ${glow ? "is-winner" : ""}`}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        {badge}
      </motion.span>
    )}
  </motion.div>
);

// ---------------------------------------------------------------------------
// 1. Place stones (counting)
// ---------------------------------------------------------------------------

const PlaceStones = ({ challenge, actions, sounds }) => {
  const slots = pathSlots(challenge.count);
  const [placed, setPlaced] = useState([]);
  const [pop, setPop] = useState(null);
  const finished = useRef(false);

  const place = (index, slot) => {
    if (finished.current || placed.includes(index)) return;
    const next = [...placed, index];
    setPlaced(next);
    const n = next.length;
    actions.placeStone(slot);
    actions.splashAt(slot);
    sounds.playPop(n / challenge.count);
    sounds.speak(numberWord(n));
    setPop({ key: `p-${n}`, number: n, unit: slot });
    if (n >= challenge.count) {
      finished.current = true;
      (async () => {
        await sleep(950);
        sounds.speak(challenge.successLine);
        await sleep(700);
        actions.onSolved();
      })();
    }
  };

  return (
    <>
      <AnimatePresence>
        {slots.map((slot, index) =>
          placed.includes(index) ? null : (
            <SlotGlow key={index} unit={slot} onTap={() => place(index, slot)} label="Place a stone here" />
          )
        )}
      </AnimatePresence>
      <CountPop pop={pop} />
      <div className="cc-chip">
        <img src="/assets/games/critter-crossing/cc_stone.png" alt="" draggable="false" />
        {placed.length} of {challenge.count} stones
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// 2. Add one more (simple addition)
// ---------------------------------------------------------------------------

const AddOneMore = ({ challenge, actions, sounds }) => {
  const slots = pathSlots(challenge.total);
  const gapSlot = slots[slots.length - 1];
  const [gapReady, setGapReady] = useState(false);
  const [finished, setFinished] = useState(false);
  const [pop, setPop] = useState(null);
  const idsRef = useRef([]);
  const doneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await sleep(2600);
      for (let i = 0; i < challenge.existing; i += 1) {
        if (cancelled) return;
        const id = actions.placeStone(slots[i]);
        idsRef.current.push(id);
        actions.splashAt(slots[i]);
        sounds.playPop((i + 1) / challenge.total);
        sounds.speak(numberWord(i + 1));
        setPop({ key: `s-${i}`, number: i + 1, unit: slots[i] });
        await sleep(800);
      }
      if (!cancelled) setGapReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placeFinal = () => {
    if (!gapReady || doneRef.current) return;
    doneRef.current = true;
    setFinished(true);
    const id = actions.placeStone(gapSlot);
    idsRef.current.push(id);
    actions.splashAt(gapSlot);
    sounds.playPop(0.9);
    (async () => {
      await sleep(550);
      for (let i = 0; i < idsRef.current.length; i += 1) {
        actions.bounceStone(idsRef.current[i]);
        sounds.speak(numberWord(i + 1));
        sounds.playPop((i + 1) / idsRef.current.length);
        setPop({ key: `r-${i}`, number: i + 1, unit: slots[i] });
        await sleep(720);
      }
      sounds.speak(challenge.successLine);
      await sleep(800);
      actions.onSolved();
    })();
  };

  return (
    <>
      <AnimatePresence>
        {gapReady && !finished && <SlotGlow unit={gapSlot} onTap={placeFinal} label="Add the last stone" />}
      </AnimatePresence>
      <CountPop pop={pop} />
      <div className={`cc-equation ${finished ? "is-done" : ""}`}>
        {challenge.existing} + 1 = {finished ? `${challenge.total}!` : "?"}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// 3. Compare groups (which has more / fewer)
// ---------------------------------------------------------------------------

const CompareGroups = ({ challenge, dims, actions, sounds }) => {
  const [solved, setSolved] = useState(false);
  const [showCounts, setShowCounts] = useState(false);
  const [wiggle, setWiggle] = useState({ left: 0, right: 0 });
  const padWidth = Math.min(dims.w, dims.h) * 0.36;

  const tap = (side) => {
    if (solved) return;
    const count = side === "left" ? challenge.leftCount : challenge.rightCount;
    const isWinner = count === challenge.winningCount;
    if (isWinner) {
      setSolved(true);
      setShowCounts(true);
      sounds.playPop(0.85);
      sounds.speak(challenge.successLine);
      (async () => {
        await sleep(1800);
        actions.onSolved();
      })();
    } else {
      actions.onMistake();
      setShowCounts(true);
      setWiggle((w) => ({ ...w, [side]: w[side] + 1 }));
      sounds.playThud();
      if (challenge.missLine) sounds.speak(challenge.missLine);
    }
  };

  const renderPad = (side) => {
    const count = side === "left" ? challenge.leftCount : challenge.rightCount;
    const isWinner = count === challenge.winningCount;
    return (
      <LilyPad
        unit={side === "left" ? LAYOUT.leftPad : LAYOUT.rightPad}
        padWidth={padWidth}
        count={count}
        itemAsset={challenge.itemAsset}
        itemName={challenge.itemName}
        badge={showCounts ? count : null}
        glow={solved && isWinner}
        celebrate={solved && isWinner}
        wiggleKey={wiggle[side]}
        onTap={() => tap(side)}
      />
    );
  };

  return (
    <>
      {renderPad("left")}
      {renderPad("right")}
    </>
  );
};

// ---------------------------------------------------------------------------
// 4. Color pattern
// ---------------------------------------------------------------------------

const ColorPattern = ({ challenge, actions, sounds }) => {
  const slots = pathSlots(challenge.pattern.length);
  const gapSlot = slots[slots.length - 1];
  const [shown, setShown] = useState(0);
  const [finished, setFinished] = useState(false);
  const [wiggles, setWiggles] = useState({});
  const idsRef = useRef([]);
  const doneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await sleep(2300);
      for (let i = 0; i < challenge.shown.length; i += 1) {
        if (cancelled) return;
        const color = challenge.shown[i];
        const id = actions.placeStone(slots[i], color);
        idsRef.current.push(id);
        actions.splashAt(slots[i]);
        sounds.playPop(STONE_COLORS[color].pitch);
        sounds.speak(color);
        setShown(i + 1);
        await sleep(750);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patternReady = shown >= challenge.shown.length;

  const pick = (color) => {
    if (!patternReady || doneRef.current) return;
    if (color === challenge.answer) {
      doneRef.current = true;
      setFinished(true);
      const id = actions.placeStone(gapSlot, color);
      idsRef.current.push(id);
      actions.splashAt(gapSlot);
      sounds.playPop(STONE_COLORS[color].pitch);
      (async () => {
        await sleep(400);
        sounds.speak(challenge.successLine);
        for (let i = 0; i < idsRef.current.length; i += 1) {
          actions.bounceStone(idsRef.current[i]);
          const c = challenge.pattern[i];
          sounds.playPop(STONE_COLORS[c] ? STONE_COLORS[c].pitch : 0.5);
          await sleep(180);
        }
        await sleep(800);
        actions.onSolved();
      })();
    } else {
      actions.onMistake();
      setWiggles((w) => ({ ...w, [color]: (w[color] || 0) + 1 }));
      sounds.playThud();
      if (challenge.missLine) sounds.speak(challenge.missLine);
    }
  };

  return (
    <>
      <AnimatePresence>
        {patternReady && !finished && (
          <>
            <SlotGlow unit={gapSlot} onTap={() => {}} label="The next stone goes here" />
            <span className="cc-pattern-q" style={{ left: `${gapSlot.x * 100}%`, top: `${gapSlot.y * 100 - 10}%` }}>
              ?
            </span>
          </>
        )}
      </AnimatePresence>
      <div className="cc-tray">
        {challenge.options.map((color) => (
          <motion.button
            key={color}
            type="button"
            className="cc-tray-btn"
            onClick={() => pick(color)}
            aria-label={`${color} stone`}
            animate={wiggles[color] ? { x: [0, -9, 9, -6, 6, 0] } : {}}
            transition={{ duration: 0.4 }}
            style={{ opacity: finished && color !== challenge.answer ? 0.35 : 1 }}
            whileTap={{ scale: 0.9 }}
          >
            <img src={STONE_COLORS[color].asset} alt="" draggable="false" />
          </motion.button>
        ))}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// 5. Number hunt (numeral recognition + ordering)
// ---------------------------------------------------------------------------

const NumberHunt = ({ challenge, actions, sounds }) => {
  const slots = pathSlots(challenge.count);
  const [numbers, setNumbers] = useState([]);
  const [ready, setReady] = useState(false);
  const [next, setNext] = useState(1);
  const [finished, setFinished] = useState(false);
  const [wiggles, setWiggles] = useState({});
  const [pop, setPop] = useState(null);
  const idsRef = useRef([]);
  const nextRef = useRef(1);
  const doneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const seeded = [...Array(challenge.count)].map((_, i) => i + 1);
    for (let i = seeded.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [seeded[i], seeded[j]] = [seeded[j], seeded[i]];
    }
    setNumbers(seeded);
    (async () => {
      await sleep(2400);
      for (let i = 0; i < slots.length; i += 1) {
        if (cancelled) return;
        const id = actions.placeStone(slots[i]);
        idsRef.current.push(id);
        actions.splashAt(slots[i]);
        sounds.playPop(0.4);
        await sleep(300);
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tap = (slotIndex) => {
    if (!ready || finished || doneRef.current) return;
    const number = numbers[slotIndex];
    if (number < nextRef.current) return; // already found — no scold
    if (number === nextRef.current) {
      actions.bounceStone(idsRef.current[slotIndex]);
      actions.splashAt(slots[slotIndex]);
      sounds.playPop(number / challenge.count);
      sounds.speak(numberWord(number));
      setPop({ key: `n-${number}`, number, unit: slots[slotIndex] });
      nextRef.current += 1;
      setNext(nextRef.current);
      if (nextRef.current > challenge.count) {
        doneRef.current = true;
        setFinished(true);
        (async () => {
          await sleep(900);
          sounds.speak(challenge.successLine);
          await sleep(700);
          actions.onSolved();
        })();
      }
    } else {
      actions.onMistake();
      setWiggles((w) => ({ ...w, [slotIndex]: (w[slotIndex] || 0) + 1 }));
      sounds.playThud();
      sounds.speak(findNumberLine(nextRef.current));
    }
  };

  return (
    <>
      {numbers.map((number, i) =>
        i < idsRef.current.length ? (
          <motion.button
            key={i}
            type="button"
            className={`cc-numeral ${number < next ? "is-found" : ""}`}
            style={pct(slots[i])}
            onClick={() => tap(i)}
            aria-label={number < next ? `Number ${number}, found` : `Number ${number} stone`}
            animate={wiggles[i] ? { x: [0, -9, 9, -6, 6, 0] } : {}}
            transition={{ duration: 0.4 }}
          >
            {number}
            {number < next && <span className="cc-numeral-check">✓</span>}
          </motion.button>
        ) : null
      )}
      <CountPop pop={pop} />
      <div className={`cc-chip ${finished ? "is-done" : ""}`}>
        {finished ? "✓ All found!" : `Find number ${Math.min(next, challenge.count)}`}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// 6. How many? (cardinality)
// ---------------------------------------------------------------------------

const CountAnswer = ({ challenge, dims, actions, sounds }) => {
  const [revealed, setRevealed] = useState(0);
  const [optionsReady, setOptionsReady] = useState(false);
  const [finished, setFinished] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [wiggles, setWiggles] = useState({});
  const busyRef = useRef(false);
  const doneRef = useRef(false);
  const padWidth = Math.min(dims.w, dims.h) * 0.42;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await sleep(2400);
      for (let i = 1; i <= challenge.count; i += 1) {
        if (cancelled) return;
        setRevealed(i);
        sounds.playPop(i / (challenge.count + 1));
        sounds.speak(numberWord(i));
        await sleep(780);
      }
      await sleep(300);
      if (!cancelled) setOptionsReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recount = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (challenge.missLine) sounds.speak(challenge.missLine);
    await sleep(1800);
    for (let i = 1; i <= challenge.count; i += 1) {
      sounds.playPop(i / (challenge.count + 1));
      sounds.speak(numberWord(i));
      await sleep(800);
    }
    busyRef.current = false;
  }, [challenge.count, challenge.missLine, sounds]);

  const pick = (option) => {
    if (!optionsReady || finished || busyRef.current || doneRef.current) return;
    if (option === challenge.count) {
      doneRef.current = true;
      setFinished(true);
      setCelebrate(true);
      sounds.playPop(0.85);
      sounds.speak(challenge.successLine);
      (async () => {
        await sleep(1400);
        actions.onSolved();
      })();
    } else {
      actions.onMistake();
      setWiggles((w) => ({ ...w, [option]: (w[option] || 0) + 1 }));
      sounds.playThud();
      recount();
    }
  };

  return (
    <>
      <LilyPad
        unit={LAYOUT.countPad}
        padWidth={padWidth}
        count={revealed}
        itemAsset={challenge.itemAsset}
        itemName={challenge.itemName}
        badge={null}
        glow={finished}
        celebrate={celebrate}
        wiggleKey={0}
      />
      <AnimatePresence>
        {optionsReady && (
          <motion.div
            className="cc-tray"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
          >
            {challenge.options.map((option) => (
              <motion.button
                key={option}
                type="button"
                className={`cc-num-btn ${finished && option === challenge.count ? "is-answer" : ""}`}
                onClick={() => pick(option)}
                aria-label={String(option)}
                animate={wiggles[option] ? { x: [0, -9, 9, -6, 6, 0] } : {}}
                transition={{ duration: 0.4 }}
                style={{ opacity: finished && option !== challenge.count ? 0.35 : 1 }}
                whileTap={{ scale: 0.9 }}
              >
                {option}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ---------------------------------------------------------------------------
// Challenge layer — routes to the right template and shows the prompt
// ---------------------------------------------------------------------------

const ChallengeLayer = ({ task, dims, sounds, actions }) => {
  const { challenge } = task;

  useEffect(() => {
    sounds.speak(challenge.prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  const shared = { challenge, dims, actions, sounds };

  return (
    <motion.div
      className="cc-challenge"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <PromptCapsule text={challenge.prompt} unit={LAYOUT.waitingSpot} />
      <div className="cc-challenge-stage">
        {challenge.type === "placeStones" && <PlaceStones {...shared} />}
        {challenge.type === "addOneMore" && <AddOneMore {...shared} />}
        {challenge.type === "compareGroups" && <CompareGroups {...shared} />}
        {challenge.type === "colorPattern" && <ColorPattern {...shared} />}
        {challenge.type === "numberHunt" && <NumberHunt {...shared} />}
        {challenge.type === "countAnswer" && <CountAnswer {...shared} />}
      </div>
    </motion.div>
  );
};

export default ChallengeLayer;
